import { PurchaseTypeEnum } from 'api-hooks/order/common/model';
import { CruisePurchaseModel } from 'api-hooks/order/cruise';
import { PurchaseRelatedModel } from 'api-hooks/sale/common';
import { CruiseSaleModel } from 'api-hooks/sale/cruise';
import { themedStyled } from 'common/styles/styletron.theme';
import { generateId } from 'common/utils/string';
import Separator from 'components/common/separator';
import { Input } from 'components/elements';
import { useFormState } from 'components/elements/form';
import Text from 'components/elements/text';
import AgentSelectInput from 'legacy-modules/master/agent/components/select-input';
import PaymentChannelSelectInput from 'legacy-modules/master/payment-channel/components/select-input';
import AgentSupendToast from 'legacy-modules/order/component/agent-suspend-toast';
import PurchaseRelatedTable from 'legacy-modules/sales/component/purchase-related-table';
import TransactionAt from 'legacy-modules/sales/component/transaction-at';
import CruiseSaleSelectInput from 'legacy-modules/sales/cruise/components/select-input';
import { guestNameTransformer } from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import {
  CardSection,
  HalfContainer,
  // OneQuarterContainer,
  SectionContainer as RawSectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import TourListSelectInput from 'legacy-modules/tour/tour-list/components/select-input';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import {
  CruisePurchaseFormType,
  CruisePurchaseItemFormType,
} from './form-type';

interface Props {
  cruisePurchase?: CruisePurchaseModel;
}

interface PurchaseDetailCardProps extends Props {
  setPurchases: React.Dispatch<React.SetStateAction<PurchaseRelatedModel[]>>;
}

function PurchaseDetailCardComponent(props: PurchaseDetailCardProps) {
  const { t } = useTranslation();
  const { setValue, control, trigger, getValues, reset } =
    useFormContext<CruisePurchaseFormType>();
  const [type, cruiseSale, total, remainingTransactionQuota] = useWatch({
    control,
    name: ['type', 'cruiseSale', 'total', 'remainingTransactionQuota'],
  });

  const { editable } = useFormState();

  const isExceedingQuota =
    remainingTransactionQuota && total > remainingTransactionQuota;

  React.useEffect(() => {
    setValue('isExceedingQuota', isExceedingQuota || false);
  }, [setValue, isExceedingQuota]);

  const invoiceId = getValues('invoiceId');
  React.useEffect(() => {
    if (!invoiceId || invoiceId === '') {
      reset();
      props.setPurchases([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, reset]);

  const onAfterDetailChangeInvoice = React.useCallback(
    (value: CruiseSaleModel) => {
      if (editable) {
        if (!props.cruisePurchase) {
          props.setPurchases(value.purchases);
        }

        const cruisePurchases: CruisePurchaseItemFormType[] =
          value.cruiseSales.map((cs) => {
            const cruisePurchaseAddOns: CruisePurchaseItemFormType['cruisePurchaseAddOns'] =
              cs.cruiseSaleAddOns.map((addon) => {
                const guest = addon.guest;
                return {
                  customId: generateId(),
                  id: addon.id,
                  description: addon.description || '',
                  price: addon.price,
                  guestId: guest.id,
                  guestName: guestNameTransformer(guest),
                };
              });

            const cruisePurchaseGuests: CruisePurchaseItemFormType['cruisePurchaseGuests'] =
              cs.cruiseSaleGuests.map((guest) => {
                const _guest = guest.guest;
                return {
                  customId: generateId(),
                  id: guest.id,
                  guestId: _guest.id,
                  guestName: guestNameTransformer(_guest),
                  guestDetailId: _guest.id,
                };
              });

            const cruisePurchaseDetails: CruisePurchaseItemFormType['cruisePurchaseDetails'] =
              cs.cruiseSaleDetails.map((detail) => {
                const guest = cs.cruiseSaleGuests.find(
                  (csg) => csg.id === detail.cruiseSaleGuestId,
                )?.guest;
                return {
                  customId: generateId(),
                  id: detail.id,
                  guestId: guest?.id || '',
                  guestName: !cs.isGroupedPrice
                    ? guestNameTransformer(guest!)
                    : '',
                  basicFare: detail.basicFare,
                  portCharge: detail.portCharge,
                  tax: detail.tax,
                  tipping: detail.tipping,
                  description: detail.description || '',
                  cruiseRoomId: detail.cruiseRoom.id,
                  cruiseRoomName: `${detail.cruiseRoom.type} (${detail.cruiseRoom.size})`,
                  cruiseRoomNumber: detail.cruiseRoomNumber,
                  deck: detail.deck || '',
                  guestDetailId: guest?.id || '',
                };
              });

            return {
              customId: generateId(),
              id: cs.id,
              bookingCode: cs.bookingCode || '',
              cruiseId: cs.cruise.id,
              cruiseName: cs.cruise.name,
              cruiseShipId: cs.cruiseShip?.id || '',
              cruiseShipName: cs.cruiseShip?.name || '',
              isGroupedPrice: cs.isGroupedPrice,
              startedAt: cs.startedAt,
              endedAt: cs.endedAt,
              reminder: cs.reminder,
              route: cs.route || '',
              cruisePurchaseAddOns,
              cruisePurchaseGuests,
              cruisePurchaseDetails,
            };
          }) ?? [];

        setValue('cruisePurchases', cruisePurchases);
        setValue('discount', value.discount);
        setValue('otherCost', value.otherCost);
        setValue('saleTotal', value.total);
        setValue('invoiceRefId', value.refId);

        trigger([
          'cruisePurchases',
          'discount',
          'otherCost',
          'saleTotal',
          'invoiceRefId',
        ]);
      }
    },
    [editable, props, setValue, trigger],
  );

  const onEnumAfterChange = React.useCallback(() => {
    props.setPurchases([]);
    setValue('invoiceRefId', undefined);
    setValue('tourRefId', undefined);
    trigger(['invoiceRefId', 'tourRefId']);
  }, [props, setValue, trigger]);

  const agentId = getValues('agentId');
  React.useEffect(() => {
    if (!agentId || agentId === '') {
      setValue('agentStatus', '');
      setValue('remainingTransactionQuota', null);
      setValue('isExceedingQuota', false);
    }
  }, [agentId, setValue]);

  const onAfterAgentDetailChange = React.useCallback(
    (result) => {
      if (editable) {
        const remainingTransactionQuota =
          result.system.remainingTransactionQuota;
        const isExceedingQuota =
          result.system.isTransactionLimited &&
          total > remainingTransactionQuota;

        setValue('isExceedingQuota', isExceedingQuota);
        setValue(
          'remainingTransactionQuota',
          remainingTransactionQuota || null,
        );
        setValue('agentStatus', result.status);
      }
    },
    [editable, setValue, total],
  );

  const disabled = !!props.cruisePurchase || !!cruiseSale;

  return (
    <CardSection>
      <Text variant="HeadingXSmall">{t('ticketOrders:orders-details')}</Text>
      <SeparatorVertical $margin={24} />
      <SectionContainer>
        {props.cruisePurchase && (
          <HalfContainer>
            <Input
              type="text"
              name="orderNumber"
              label={t('common:order_number')}
              placeholder={t('common:order_number')}
              disabled
            />
          </HalfContainer>
        )}
        <HalfContainer
          $style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
          }}
        >
          <TransactionAt
            name="transactionAt"
            clearable={false}
            hasTime
            required
          />
        </HalfContainer>
      </SectionContainer>
      <SectionContainer>
        <HalfContainer>
          <Input
            type="select"
            label={t('common:type')}
            placeholder={t('common:type')}
            name="type"
            required
            onAfterChange={onEnumAfterChange}
            clearable={false}
            disabled={disabled}
            options={[
              { id: PurchaseTypeEnum.Sales, label: t('common:sales') },
              { id: PurchaseTypeEnum.Tour, label: t('common:tour') },
            ]}
          />
        </HalfContainer>
        <HalfContainer>
          {type === PurchaseTypeEnum.Sales ? (
            <CruiseSaleSelectInput
              label={t('common:invoice_number')}
              placeholder={t('common:choose_invoice')}
              name="invoiceId"
              onAfterDetailChange={onAfterDetailChangeInvoice}
              disabled={disabled}
              type="invoice"
              required
            />
          ) : (
            type === PurchaseTypeEnum.Tour && (
              <TourListSelectInput
                name="tourId"
                disabled={!!props.cruisePurchase}
                required
                onAfterDetailChange={(value) => {
                  setValue('tourRefId', value.refId);
                }}
              />
            )
          )}
        </HalfContainer>
      </SectionContainer>
      <SectionContainer>
        <HalfContainer>
          <AgentSelectInput
            name="agentId"
            remainingTransactionQuota={remainingTransactionQuota}
            onAfterDetailChange={onAfterAgentDetailChange}
            required
          />
        </HalfContainer>
        <HalfContainer>
          <PaymentChannelSelectInput name="paymentChannelId" />
        </HalfContainer>
      </SectionContainer>
      <AgentSupendToast />
    </CardSection>
  );
}

export default function PurchasesDetailCard(props: Props) {
  const { t } = useTranslation();
  const [purchases, setPurchases] = React.useState<PurchaseRelatedModel[]>([]);
  const { control } = useFormContext<CruisePurchaseFormType>();
  const [type] = useWatch({
    control,
    name: ['type'],
  });

  return (
    <>
      <PurchaseDetailCardComponent {...props} setPurchases={setPurchases} />
      {type === PurchaseTypeEnum.Sales &&
        !props.cruisePurchase &&
        !!purchases.length && (
          <>
            <Separator gap={24} />
            <Text variant="HeadingXSmall">{t('common:related_purchases')}</Text>
            <Separator gap={8} />
            <PurchaseRelatedTable
              data={purchases}
              navigation="CruisePurchasesView"
            />
          </>
        )}
      <Separator gap={24} />
    </>
  );
}

const SectionContainer = themedStyled(RawSectionContainer, {
  width: '100%',
});
